import React, { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import ImageLightbox from './ImageLightbox';
import { classifyAttachment } from './attachmentKind';
import { downloadAttachment } from './downloadAttachment';
import { PREVIEW_MAX_BYTES } from './AttachmentTray.constants';
import { PDF_PREVIEW_MAX_BYTES } from '@components/PdfViewer/PdfViewer.constants';
import type { PreviewableAttachment } from './types';

// Lazy so neither pdf.js nor three.js reaches the entry bundle for the many
// sessions that never open one.
const PdfPreviewModal = lazy(() => import('./PdfPreviewModal'));
const ModelPreviewModal = lazy(() => import('./ModelPreviewModal'));

type Activation = 'image' | 'pdf' | 'model' | 'download' | null;

/**
 * What clicking an attachment does. Anything with no viewer downloads instead,
 * which is the honest answer: the file is still the user's to open.
 */
const activationFor = (item: PreviewableAttachment): Activation => {
  if (!item.source) return null;
  if (item.fileSize > PREVIEW_MAX_BYTES) return 'download';
  switch (classifyAttachment(item.contentType, item.filename)) {
    case 'image':
      return 'image';
    case 'pdf':
      return item.fileSize > PDF_PREVIEW_MAX_BYTES ? 'download' : 'pdf';
    case 'model':
      return 'model';
    default:
      return 'download';
  }
};

export interface AttachmentPreview {
  /** The click handler for an item, or undefined when it has nowhere to go. */
  activate: (item: PreviewableAttachment) => (() => void) | undefined;
  /** The open viewer, if any. Render it once, anywhere in the tree. */
  overlay: React.ReactNode;
}

/**
 * Opening attached files, shared by every surface that lists them, because
 * which viewer a file gets is a property of the file rather than of the screen
 * it was listed on.
 *
 * <p>Tracked by attachment key rather than by index, so an open viewer closes
 * itself when its attachment goes away instead of silently showing the next
 * one along.
 */
export const useAttachmentPreview = (
  items: readonly PreviewableAttachment[],
): AttachmentPreview => {
  const [lightboxKey, setLightboxKey] = useState<string | null>(null);
  const [pdfKey, setPdfKey] = useState<string | null>(null);
  const [modelKey, setModelKey] = useState<string | null>(null);

  // The lightbox pages through the images beside the one that was clicked, so
  // it needs them all rather than only the chosen one.
  const imageItems = useMemo(
    () => items.filter((item) => activationFor(item) === 'image'),
    [items],
  );

  const lightboxIndex = imageItems.findIndex((item) => item.key === lightboxKey);
  const pdfItem = items.find((item) => item.key === pdfKey);
  const modelItem = items.find((item) => item.key === modelKey);

  const activate = useCallback((item: PreviewableAttachment): (() => void) | undefined => {
    const activation = activationFor(item);
    switch (activation) {
      case 'image':
        return () => setLightboxKey(item.key);
      case 'pdf':
        return () => setPdfKey(item.key);
      case 'model':
        return () => setModelKey(item.key);
      case 'download':
        return () => void downloadAttachment(item);
      case null:
        return undefined;
      default: {
        const exhaustive: never = activation;
        return exhaustive;
      }
    }
  }, []);

  const overlay = (
    <>
      {lightboxIndex >= 0 && (
        <ImageLightbox
          items={imageItems}
          startIndex={lightboxIndex}
          onClose={() => setLightboxKey(null)}
        />
      )}
      {pdfItem && (
        <Suspense fallback={null}>
          <PdfPreviewModal item={pdfItem} onClose={() => setPdfKey(null)} />
        </Suspense>
      )}
      {modelItem && (
        <Suspense fallback={null}>
          <ModelPreviewModal item={modelItem} onClose={() => setModelKey(null)} />
        </Suspense>
      )}
    </>
  );

  return { activate, overlay };
};
